import { useState } from 'react';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Button, ButtonSize, ButtonType, getButtonSizeFromString } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { closeModalEvent } from '@nx-frontend/shared/ui/modal';
import { Notice } from '@nx-frontend/shared/ui/notice';
import { useHotKey } from '@nx-frontend/shared/hooks';
import { useGetPortalChanges } from '@nx-frontend/shared/hooks/use-get-portal-changes';
import { webnotesThemeColors, webnotesThemeStyle } from '@nx-frontend/shared/themes/webnotes';
import { CustomizerUnpublishedChangesPopupPayload } from '@nx-frontend/shared/types';

export interface CustomizerUnpublishedChangesPopupProps {
  content: CustomizerUnpublishedChangesPopupPayload;
  usePadding?: boolean;
}

export function CustomizerUnpublishedChangesPopup(props: CustomizerUnpublishedChangesPopupProps) {
  const closeModal = useUnit(closeModalEvent);
  useHotKey('Enter', function onEnter() {
    const mainButton = props.content.buttons.find((button) => button.main);
    if (mainButton && mainButton.onClick) {
      mainButton.onClick();
    }
  });
  const [showDropdown, setShowDropdown] = useState(false);
  const { text, buttons } = props.content;
  const changes = useGetPortalChanges();

  const buttonsComponents = buttons.map((button, i) => {
    const onClick = button.onClick || closeModal;
    const size = button.size ? getButtonSizeFromString(button.size) : ButtonSize.SM;

    return (
      <Button
        key={'button-' + i}
        type={button.main ? ButtonType.PRIMARY : ButtonType.STROKED}
        size={size}
        onClick={onClick}
        themeColors={props.content.themeColors ?? webnotesThemeColors}
        themeStyle={props.content.themeStyle ?? webnotesThemeStyle}
        className={classNames('!px-3 min-w-40', button.classes)}
      >
        {button.text}
      </Button>
    );
  });

  return (
    <div className="max-w-[500px]">
      <div
        className={classNames(
          (props.usePadding ?? true) && 'pb-4',
          'flex items-center justify-center basis-full w-full text-sm'
        )}
      >
        {text}
      </div>
      <Notice hideIcon={true} className="rounded-none py-2 px-3 border-l-2 border-red-500" type="warning">
        {"If you discard, these changes won't be saved."}
      </Notice>
      <div
        className="flex items-center gap-2 cursor-pointer font-bold text-[13px] text-cyan-900 mt-3 py-1.5"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        {showDropdown ? 'Hide changes overview' : 'Show changes overview'}
        <Icon
          icon={IconsMap.ARROW_2_UP}
          className={classNames('size-3 shrink-0 ', { 'rotate-180': !showDropdown })}
        ></Icon>
      </div>
      {showDropdown && (
        <div className="flex flex-col bg-neutral-50 rounded-md py-2 px-4 gap-1">
          <span className="h-6 flex items-center text-[10px] font-bold tracking-[0.2px] uppercase">
            Affected items (Pages, Navigation, etc.)
          </span>
          {changes.reverse().map(({ key, title, path: changePath, changes }) => {
            if (changes === 0) {
              return null;
            }
            return (
              <span key={key} className="h-6 flex items-center">
                {changePath ? '/' : ''}
                {title}
              </span>
            );
          })}
        </div>
      )}

      <div className="dialog-popup-buttons-wrapper flex flex-row justify-end gap-2 flex-wrap mt-5">
        {buttonsComponents}
      </div>
    </div>
  );
}
